import { IncomeData, LegacyIncomeData, TaxCalculation, HousingBudget, RegionalFactors, AffordabilityAnalysis, MonthlyPaymentBreakdown } from '../types';
import { CALCULATION_DEFAULTS } from './constants';

/**
 * Calculate gross annual income including overtime (legacy function)
 */
export function calculateGrossAnnualIncome(income: LegacyIncomeData): number {
  const regularAnnualIncome = income.baseHourlyRate * income.regularHours * income.weeksPerYear;
  const overtimeAnnualIncome = income.baseHourlyRate * income.overtimeMultiplier * income.overtimeHours * income.weeksPerYear;
  
  return regularAnnualIncome + overtimeAnnualIncome;
}

/**
 * Calculate gross monthly income (legacy function)
 */
export function calculateGrossMonthlyIncome(income: LegacyIncomeData): number {
  return calculateGrossAnnualIncome(income) / 12;
}

/**
 * Estimate tax calculations based on income and regional factors
 */
export function calculateTaxes(grossAnnualIncome: number, state: string): TaxCalculation {
  // Federal tax brackets (simplified 2024 estimates for single filer)
  let federalTax = 0;
  if (grossAnnualIncome > 11000) {
    federalTax += Math.min(grossAnnualIncome - 11000, 33725) * 0.12;
  }
  if (grossAnnualIncome > 44725) {
    federalTax += Math.min(grossAnnualIncome - 44725, 50000) * 0.22;
  }
  if (grossAnnualIncome > 95375) {
    federalTax += (grossAnnualIncome - 95375) * 0.24;
  }

  // State tax estimates (simplified)
  const stateTaxRates: Record<string, number> = {
    'CA': 0.08,
    'NY': 0.07,
    'TX': 0.00,
    'FL': 0.00,
    'WA': 0.00,
    'NV': 0.00,
    'TN': 0.00,
    'WY': 0.00,
    'SD': 0.00,
    'AK': 0.00,
    'NH': 0.00
  };
  
  const stateTax = grossAnnualIncome * (stateTaxRates[state] || 0.05);
  
  // FICA taxes
  const socialSecurity = Math.min(grossAnnualIncome, 160200) * 0.062; // 2024 wage base
  const medicare = grossAnnualIncome * 0.0145;
  
  const totalTax = federalTax + stateTax + socialSecurity + medicare;
  const effectiveRate = totalTax / grossAnnualIncome;

  return {
    federalTax,
    stateTax,
    socialSecurity,
    medicare,
    totalTax,
    effectiveRate
  };
}

/**
 * Calculate net monthly income after taxes
 */
export function calculateNetMonthlyIncome(grossAnnualIncome: number, taxes: TaxCalculation): number {
  const netAnnualIncome = grossAnnualIncome - taxes.totalTax;
  return netAnnualIncome / 12;
}

/**
 * Calculate housing budget recommendations
 */
export function calculateHousingBudget(
  grossMonthlyIncome: number,
  netMonthlyIncome: number,
  _regional: RegionalFactors
): HousingBudget {
  // Use 30% of gross income as standard recommendation
  const recommendedHousingBudget = grossMonthlyIncome * 0.30;
  
  // Max rent should not exceed recommended budget
  const maxRentBudget = recommendedHousingBudget;
  
  // Estimate home purchase price based on 28% debt-to-income ratio
  // Assuming 6.5% interest rate, 30-year mortgage, property tax, insurance
  const monthlyPITI = grossMonthlyIncome * 0.28;
  
  // Approximate home price calculation (simplified)
  // Monthly payment = Principal + Interest + Taxes + Insurance
  const monthlyPI = monthlyPITI * 0.85; // Assuming taxes/insurance are ~15% of payment
  const maxHomePurchasePrice = monthlyPI * 166; // Rough calculation for 6.5% 30-year loan
  
  // Recommended down payment (20% to avoid PMI)
  const recommendedDownPayment = maxHomePurchasePrice * 0.20;
  
  // Estimated mortgage payment including PITI
  const estimatedMortgagePayment = monthlyPITI;
  
  // Remaining income for other expenses
  const remainingForOtherExpenses = netMonthlyIncome - recommendedHousingBudget;

  return {
    grossMonthlyIncome,
    netMonthlyIncome,
    recommendedHousingBudget,
    maxRentBudget,
    maxHomePurchasePrice,
    recommendedDownPayment,
    estimatedMortgagePayment,
    remainingForOtherExpenses
  };
}

/**
 * Format currency values for display
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
}

/**
 * Format percentage values for display
 */
export function formatPercentage(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1
  }).format(value);
}

/**
 * Validate legacy income input data
 */
export function validateIncomeData(income: Partial<LegacyIncomeData>): string[] {
  const errors: string[] = [];
  
  if (!income.baseHourlyRate || income.baseHourlyRate <= 0) {
    errors.push('Base hourly rate must be greater than 0');
  }
  
  if (!income.regularHours || income.regularHours <= 0 || income.regularHours > 80) {
    errors.push('Regular hours must be between 1 and 80 per week');
  }
  
  if (income.overtimeHours && (income.overtimeHours < 0 || income.overtimeHours > 60)) {
    errors.push('Overtime hours must be between 0 and 60 per week');
  }
  
  if (!income.overtimeMultiplier || income.overtimeMultiplier < 1 || income.overtimeMultiplier > 3) {
    errors.push('Overtime multiplier must be between 1 and 3');
  }
  
  return errors;
}

/**
 * NEW SPECIFICATION FUNCTIONS
 */

/**
 * Calculate total gross household income based on specification
 */
export function calculateTotalGrossIncome(income: IncomeData): number {
  // Total Gross Income: =B6*(1+B7)+B8+B9+(B10*12)
  const primaryWithOvertime = income.primaryEarnerBaseSalary * (1 + income.overtimeFactor);
  const sideWorkAnnual = income.sideWorkFreelanceMonthly * 12;
  
  return primaryWithOvertime + income.bonusIncentiveAnnual + income.secondaryEarnerSalary + sideWorkAnnual;
}

/**
 * Calculate adjusted monthly income
 */
export function calculateAdjustedMonthlyIncome(income: IncomeData): number {
  // Adjusted Monthly: =(Total_Gross/12)*B11
  const totalGross = calculateTotalGrossIncome(income);
  return (totalGross / 12) * income.regionalCostAdjustmentFactor;
}

/**
 * Calculate affordability analysis using trade worker ratios
 */
export function calculateAffordabilityAnalysis(monthlyIncome: number): AffordabilityAnalysis {
  const tradeWorkerRatio30 = monthlyIncome * CALCULATION_DEFAULTS.tradeWorkerRatio;
  const conservativeRatio25 = monthlyIncome * CALCULATION_DEFAULTS.conservativeRatio;
  const maxMonthlyPayment30 = tradeWorkerRatio30;
  
  // Calculate max home prices for different loan types
  const fhaMaxPrice = calculateMaxHomePriceFromPayment(maxMonthlyPayment30, CALCULATION_DEFAULTS.fhaDownPayment, CALCULATION_DEFAULTS.fhaMipRate);
  const conventionalMaxPrice = calculateMaxHomePriceFromPayment(maxMonthlyPayment30, CALCULATION_DEFAULTS.conventionalDownPayment, CALCULATION_DEFAULTS.pmiRate);
  const vaMaxPrice = calculateMaxHomePriceFromPayment(maxMonthlyPayment30, CALCULATION_DEFAULTS.vaDownPayment, 0);
  
  return {
    monthlyGrossIncome: monthlyIncome,
    tradeWorkerRatio30,
    conservativeRatio25,
    maxMonthlyPayment30,
    fhaMaxPrice,
    conventionalMaxPrice,
    vaMaxPrice
  };
}

/**
 * Calculate maximum home price from monthly payment using PMT formula
 */
export function calculateMaxHomePriceFromPayment(
  maxPayment: number, 
  downPaymentPercent: number, 
  _pmiRate: number = 0
): number {
  const interestRate = CALCULATION_DEFAULTS.mortgageInterestRate;
  const termMonths = CALCULATION_DEFAULTS.mortgageTermYears * 12;
  
  // Estimate property tax and insurance (simplified)
  // const taxInsuranceRatio = CALCULATION_DEFAULTS.propertyTaxRate / 12 + CALCULATION_DEFAULTS.homeInsuranceRate / 12;
  
  // Available for principal & interest (subtracting estimated tax/insurance)
  const availableForPI = maxPayment * 0.85; // Rough estimate
  
  // PMT formula: payment = principal * [r(1+r)^n] / [(1+r)^n - 1]
  // Rearranged: principal = payment * [(1+r)^n - 1] / [r(1+r)^n]
  const monthlyRate = interestRate / 12;
  const principal = availableForPI * ((Math.pow(1 + monthlyRate, termMonths) - 1) / (monthlyRate * Math.pow(1 + monthlyRate, termMonths)));
  
  // Calculate home price from loan amount
  const homePrice = principal / (1 - downPaymentPercent);
  
  return Math.max(0, homePrice);
}

/**
 * Calculate monthly payment breakdown for specific loan scenario
 */
export function calculateMonthlyPaymentBreakdown(
  homePrice: number,
  downPaymentPercent: number,
  monthlyIncome: number,
  propertyTaxRate: number = CALCULATION_DEFAULTS.propertyTaxRate,
  insuranceRate: number = CALCULATION_DEFAULTS.homeInsuranceRate,
  pmiRate: number = CALCULATION_DEFAULTS.pmiRate
): MonthlyPaymentBreakdown {
  const downPayment = homePrice * downPaymentPercent;
  const loanAmount = homePrice - downPayment;
  
  // Calculate P&I using PMT formula
  const monthlyRate = CALCULATION_DEFAULTS.mortgageInterestRate / 12;
  const termMonths = CALCULATION_DEFAULTS.mortgageTermYears * 12;
  const principalAndInterest = loanAmount * (monthlyRate * Math.pow(1 + monthlyRate, termMonths)) / (Math.pow(1 + monthlyRate, termMonths) - 1);
  
  const propertyTaxMonthly = (homePrice * propertyTaxRate) / 12;
  const homeownersInsurance = (homePrice * insuranceRate) / 12;
  
  // PMI/MIP calculation
  let pmiOrMip = 0;
  if (downPaymentPercent < 0.20 && downPaymentPercent !== 0) {
    if (downPaymentPercent === CALCULATION_DEFAULTS.fhaDownPayment) {
      // FHA MIP
      pmiOrMip = (loanAmount * CALCULATION_DEFAULTS.fhaMipRate) / 12;
    } else {
      // Conventional PMI
      pmiOrMip = (loanAmount * pmiRate) / 12;
    }
  }
  
  const totalMonthlyPayment = principalAndInterest + propertyTaxMonthly + homeownersInsurance + pmiOrMip;
  const remainingBudget = monthlyIncome - totalMonthlyPayment;
  const budgetUtilization = (totalMonthlyPayment / monthlyIncome) * 100;
  
  return {
    homePrice,
    downPayment,
    loanAmount,
    principalAndInterest,
    propertyTaxMonthly,
    homeownersInsurance,
    pmiOrMip,
    totalMonthlyPayment,
    remainingBudget,
    budgetUtilization
  };
}

/**
 * Validate new income data structure
 */
export function validateNewIncomeData(income: Partial<IncomeData>): string[] {
  const errors: string[] = [];
  
  if (!income.primaryEarnerBaseSalary || income.primaryEarnerBaseSalary < 25000 || income.primaryEarnerBaseSalary > 150000) {
    errors.push('Primary earner salary must be between $25,000 and $150,000');
  }
  
  if (income.overtimeFactor !== undefined && (income.overtimeFactor < 0 || income.overtimeFactor > 0.40)) {
    errors.push('Overtime factor must be between 0% and 40%');
  }
  
  if (income.bonusIncentiveAnnual !== undefined && income.bonusIncentiveAnnual < 0) {
    errors.push('Bonus/incentive amount cannot be negative');
  }
  
  if (income.secondaryEarnerSalary !== undefined && income.secondaryEarnerSalary < 0) {
    errors.push('Secondary earner salary cannot be negative');
  }
  
  if (income.sideWorkFreelanceMonthly !== undefined && income.sideWorkFreelanceMonthly < 0) {
    errors.push('Side work income cannot be negative');
  }
  
  if (income.regionalCostAdjustmentFactor !== undefined && (income.regionalCostAdjustmentFactor < 0.75 || income.regionalCostAdjustmentFactor > 1.35)) {
    errors.push('Regional cost adjustment factor must be between 0.75 and 1.35');
  }
  
  return errors;
}

/**
 * Calculate purchasing power index for market comparison
 */
export function calculatePurchasingPowerIndex(localIncome: number, costOfLivingIndex: number): number {
  return (localIncome / costOfLivingIndex) * 100;
}

/**
 * Calculate affordability score (1-10 scale)
 */
export function calculateAffordabilityScore(maxAffordablePrice: number, medianPrice: number, remainingBudget: number, totalIncome: number): number {
  const priceRatio = (maxAffordablePrice / medianPrice) * 5;
  const budgetRatio = (remainingBudget / totalIncome) * 5;
  return Math.min(10, Math.max(0, priceRatio + budgetRatio));
}

/**
 * Generate recommendations based on calculation results
 */
export function generateRecommendations(
  income: IncomeData | LegacyIncomeData,
  housing: HousingBudget,
  regional: RegionalFactors
): string[] {
  const recommendations: string[] = [];
  
  // Check if using new or legacy income structure
  const isNewStructure = 'primaryEarnerBaseSalary' in income;
  const monthlyIncome = isNewStructure 
    ? calculateAdjustedMonthlyIncome(income as IncomeData)
    : calculateGrossMonthlyIncome(income as LegacyIncomeData);
  
  // Income-based recommendations
  if (monthlyIncome < 4000) {
    recommendations.push('Consider increasing income through additional skills training or overtime opportunities');
  }
  
  // Housing cost recommendations
  if (housing.recommendedHousingBudget > housing.netMonthlyIncome * 0.35) {
    recommendations.push('Your housing budget may be tight after taxes - consider a lower cost area or shared housing');
  }
  
  // Emergency fund recommendations
  recommendations.push('Build an emergency fund of 3-6 months of expenses before purchasing a home');
  
  // Regional recommendations
  if (regional.cityType === 'urban') {
    recommendations.push('Consider suburban areas for better housing value and shorter commute times');
  }
  
  // Trade-specific recommendations
  recommendations.push('Factor in seasonal work variations and potential overtime changes in your long-term budget');
  recommendations.push('Research first-time homebuyer programs and VA loans if eligible');
  
  return recommendations;
}